import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { Card, CardContent, Tabs, Tab, Grid, Typography, Box } from "@mui/material";
import { BaseFormPage, BaseTabPanel } from "../../../../components/common/BaseForm";
import { useFormCompletion, useFormSubmission } from "../../../../hooks";
import {
  vehicleValidationSchema,
  VehicleFormValues,
  VehicleOwnershipType as FormVehicleOwnershipType
} from "../../validation/vehicleValidationSchema";
import {
  useGetVehicleWithDetailsQuery,
  useUpdateVehicleWithDetailsMutation,
  useCreateVehicleMutation,
  UpdateVehicleRequest,
  CreateVehicleRequest,
} from "../../../../api/vehiclesApi";

// Import all tab components
import GeneralTab from "./tabs/GeneralTab";
import TruckInfoTab from "./tabs/TruckInfoTab";
import OwnershipTab from "./tabs/OwnershipTab";
import SafetyTab from "./tabs/SafetyTab";
// import IftaTab from "./tabs/IftaTab";
import DispatchTab from "./tabs/DispatchTab";
import GpsTab from "./tabs/GpsTab";
import OdometerTab from "./tabs/OdometerTab";
import CommentsTab from "./tabs/CommentsTab";
import VehicleDocumentsTab from "../VehicleDocumentsTab";
import VehiclePtiHistoryTab from "../VehiclePtiHistoryTab";

export interface VehicleFormLayoutProps {
  mode: "create" | "edit";
  entityId?: string;
  title: string;
  onSuccess: () => void;
}

const VehicleFormLayout: React.FC<VehicleFormLayoutProps> = ({
  mode,
  entityId,
  title,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const vehicleId = entityId ? parseInt(entityId, 10) : 0;

  // API hooks
  const {
    data: vehicle,
    isLoading,
    error,
  } = useGetVehicleWithDetailsQuery(vehicleId, { skip: mode === "create" });

  const [updateVehicle] = useUpdateVehicleWithDetailsMutation();
  const [createVehicle] = useCreateVehicleMutation();

  // Helper function to convert date strings to correct format
  const formatDateForForm = (dateString: string | null): string | null => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toISOString();
    } catch {
      return null;
    }
  };

  // Initialize form values
  const getInitialValues = (): VehicleFormValues => {
    if (mode === "create") {
      return {
        // Base Vehicle
        name: "",
        vin: "",
        make: "",
        model: "",
        year: "",
        // General Section
        unitNumber: "",
        isActive: true,
        startDate: null,
        endDate: null,
        // Lookup fields
        vehicleTypeId: null,
        vehicleTypeName: null,
        vehicleStatusId: null,
        vehicleStatusName: null,
        divisionId: null,
        divisionName: null,
        // Truck Info Section
        color: "",
        licensePlate: "",
        licenseState: "",
        fuelCardNumber: "",
        fuelType: "",
        iPassNumber: "",
        bestPassNumber: "",
        prePassEzPassNumber: "",
        tireSize: "",
        deductTolls: false,
        deductFuel: false,
        // Ownership Section
        ownershipType: FormVehicleOwnershipType.CompanyOwned,
        ownerInfo: null,
        leaseFromInfo: null,
        leaseToInfo: null,
        // Safety Section
        dotInspectionExpiration: null,
        platesExpirationDate: null,
        insuranceExpirationDate: null,
        insuranceValue: null,
        escrowStartingBalance: null,
        escrowAccountBalance: null,
        hasCustomWarning: false,
        // IFTA Section
        iftaHandledByCompany: false,
        iftaExpirationDate: null,
        nyPermitNumber: "",
        grossWeight: null,
        unloadedWeight: null,
        numberOfAxles: null,
        // Dispatch Section
        dispatcher: "",
        dispatchGroup: "",
        dispatchCategory: "",
        // GPS Section
        gpsType: "",
        // Odometer Section
        lastOdometerReading: null,
        lastOdometerDate: null,
        estimatedOdometer: null,
        // Comments Section
        comments: "",
      };
    }

    if (!vehicle) {
      // fallback to create mode defaults when vehicle data is not available
      return {
        // Base Vehicle
        name: "",
        vin: "",
        make: "",
        model: "",
        year: "",
        // General Section
        unitNumber: "",
        isActive: true,
        startDate: null,
        endDate: null,
        // Lookup fields
        vehicleTypeId: null,
        vehicleTypeName: null,
        vehicleStatusId: null,
        vehicleStatusName: null,
        divisionId: null,
        divisionName: null,
        // Truck Info Section
        color: "",
        licensePlate: "",
        licenseState: "",
        fuelCardNumber: "",
        fuelType: "",
        iPassNumber: "",
        bestPassNumber: "",
        prePassEzPassNumber: "",
        tireSize: "",
        deductTolls: false,
        deductFuel: false,
        // Ownership Section
        ownershipType: FormVehicleOwnershipType.CompanyOwned,
        ownerInfo: null,
        leaseFromInfo: null,
        leaseToInfo: null,
        // Safety Section
        dotInspectionExpiration: null,
        platesExpirationDate: null,
        insuranceExpirationDate: null,
        insuranceValue: null,
        escrowStartingBalance: null,
        escrowAccountBalance: null,
        hasCustomWarning: false,
        // IFTA Section
        iftaHandledByCompany: false,
        iftaExpirationDate: null,
        nyPermitNumber: "",
        grossWeight: null,
        unloadedWeight: null,
        numberOfAxles: null,
        // Dispatch Section
        dispatcher: "",
        dispatchGroup: "",
        dispatchCategory: "",
        // GPS Section
        gpsType: "",
        // Odometer Section
        lastOdometerReading: null,
        lastOdometerDate: null,
        estimatedOdometer: null,
        // Comments Section
        comments: "",
      };
    }

    return {
      // Base Vehicle
      name: vehicle.name || "",
      vin: vehicle.vin || "",
      make: vehicle.make || "",
      model: vehicle.model || "",
      year: vehicle.year || "",
      // General Section
      unitNumber: vehicle.unitNumber || "",
      isActive: vehicle.isActive || false,
      startDate: formatDateForForm(vehicle.startDate || null),
      endDate: formatDateForForm(vehicle.endDate || null),
      // Lookup fields - prioritize existing values
      vehicleTypeId: vehicle.availableVehicleTypes?.find(vt => vt.name === vehicle.vehicleType)?.id || null,
      vehicleTypeName: null,
      vehicleStatusId: vehicle.availableVehicleStatuses?.find(vs => vs.name === vehicle.vehicleStatus)?.id || null,
      vehicleStatusName: null,
      divisionId: vehicle.availableDivisions?.find(d => d.name === vehicle.division)?.id || null,
      divisionName: null,
      // Truck Info Section
      color: vehicle.color || "",
      licensePlate: vehicle.licensePlate || "",
      licenseState: vehicle.licenseState || "",
      fuelCardNumber: vehicle.fuelCardNumber || "",
      fuelType: vehicle.fuelType || "",
      iPassNumber: vehicle.iPassNumber || "",
      bestPassNumber: vehicle.bestPassNumber || "",
      prePassEzPassNumber: vehicle.prePassEzPassNumber || "",
      tireSize: vehicle.tireSize || "",
      deductTolls: vehicle.deductTolls || false,
      deductFuel: vehicle.deductFuel || false,
      // Ownership Section
      ownershipType: vehicle.ownershipType || FormVehicleOwnershipType.CompanyOwned,
      ownerInfo: vehicle.ownerInfo ? {
        company: vehicle.ownerInfo.company || '',
        firstName: vehicle.ownerInfo.firstName || '',
        lastName: vehicle.ownerInfo.lastName || '',
        address: vehicle.ownerInfo.address || '',
        city: vehicle.ownerInfo.city || '',
        state: vehicle.ownerInfo.state || '',
        zipCode: vehicle.ownerInfo.zipCode || '',
        ssnFein: vehicle.ownerInfo.ssnFein || '',
        bankAccount: vehicle.ownerInfo.bankAccount || '',
        contacts: vehicle.ownerInfo.contacts || '',
        email: vehicle.ownerInfo.email || '',
        website: vehicle.ownerInfo.website || '',
      } : {
        company: '',
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        ssnFein: '',
        bankAccount: '',
        contacts: '',
        email: '',
        website: '',
      },
      leaseFromInfo: vehicle.leaseFromInfo ? {
        lessorId: (vehicle.leaseFromInfo as any).lessorId || (vehicle.leaseFromInfo as any).LessorId || null,
        lessorName: (vehicle.leaseFromInfo as any).lessorName || (vehicle.leaseFromInfo as any).LessorName || null,
        startDate: formatDateForForm((vehicle.leaseFromInfo as any).startDate || (vehicle.leaseFromInfo as any).StartDate),
        endDate: formatDateForForm((vehicle.leaseFromInfo as any).endDate || (vehicle.leaseFromInfo as any).EndDate),
      } : null,
      leaseToInfo: vehicle.leaseToInfo ? {
        lesseeId: (vehicle.leaseToInfo as any).lesseeId || (vehicle.leaseToInfo as any).LesseeId || null,
        lesseeName: (vehicle.leaseToInfo as any).lesseeName || (vehicle.leaseToInfo as any).LesseeName || null,
        startDate: formatDateForForm((vehicle.leaseToInfo as any).startDate || (vehicle.leaseToInfo as any).StartDate),
        endDate: formatDateForForm((vehicle.leaseToInfo as any).endDate || (vehicle.leaseToInfo as any).EndDate),
      } : null,
      // Safety Section
      dotInspectionExpiration: formatDateForForm(vehicle.dotInspectionExpiration || null),
      platesExpirationDate: formatDateForForm(vehicle.platesExpirationDate || null),
      insuranceExpirationDate: formatDateForForm(vehicle.insuranceExpirationDate || null),
      insuranceValue: vehicle.insuranceValue || null,
      escrowStartingBalance: vehicle.escrowStartingBalance || null,
      escrowAccountBalance: vehicle.escrowAccountBalance || null,
      hasCustomWarning: vehicle.hasCustomWarning || false,
      // IFTA Section
      iftaHandledByCompany: vehicle.iftaHandledByCompany || false,
      iftaExpirationDate: formatDateForForm(vehicle.iftaExpirationDate || null),
      nyPermitNumber: vehicle.nyPermitNumber || "",
      grossWeight: vehicle.grossWeight || null,
      unloadedWeight: vehicle.unloadedWeight || null,
      numberOfAxles: vehicle.numberOfAxles || null,
      // Dispatch Section
      dispatcher: vehicle.dispatcher || "",
      dispatchGroup: vehicle.dispatchGroup || "",
      dispatchCategory: vehicle.dispatchCategory || "",
      // GPS Section
      gpsType: vehicle.gpsType || "",
      // Odometer Section
      lastOdometerReading: vehicle.lastOdometerReading || null,
      lastOdometerDate: formatDateForForm(vehicle.lastOdometerDate || null),
      estimatedOdometer: vehicle.estimatedOdometer || null,
      // Comments Section
      comments: vehicle.comments || "",
    };
  };

  const formik = useFormik<VehicleFormValues>({
    initialValues: getInitialValues(),
    validationSchema: vehicleValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await handleSubmit(async () => {
        if (mode === "edit") {
          const updateRequest: UpdateVehicleRequest = {
            name: values.name,
            vin: values.vin,
            make: values.make,
            model: values.model,
            year: values.year,
            unitNumber: values.unitNumber,
            isActive: values.isActive,
            startDate: values.startDate,
            endDate: values.endDate,
            vehicleTypeId: values.vehicleTypeId,
            vehicleTypeName: values.vehicleTypeName,
            vehicleStatusId: values.vehicleStatusId,
            vehicleStatusName: values.vehicleStatusName,
            divisionId: values.divisionId,
            divisionName: values.divisionName,
            color: values.color,
            licensePlate: values.licensePlate,
            licenseState: values.licenseState,
            fuelCardNumber: values.fuelCardNumber,
            fuelType: values.fuelType,
            iPassNumber: values.iPassNumber,
            bestPassNumber: values.bestPassNumber,
            prePassEzPassNumber: values.prePassEzPassNumber,
            tireSize: values.tireSize,
            deductTolls: values.deductTolls,
            deductFuel: values.deductFuel,
            ownershipType: values.ownershipType === FormVehicleOwnershipType.CompanyOwned
              ? (values.leaseFromInfo && (values.leaseFromInfo.lessorId || (values.leaseFromInfo.lessorName && values.leaseFromInfo.lessorName.trim() !== '')))
                ? FormVehicleOwnershipType.CompanyOwnedLeasedFrom
                : values.leaseToInfo && (values.leaseToInfo.lesseeId || (values.leaseToInfo.lesseeName && values.leaseToInfo.lesseeName.trim() !== ''))
                  ? FormVehicleOwnershipType.CompanyOwnedLeasedTo
                  : FormVehicleOwnershipType.CompanyOwned
              : values.ownershipType,
            ownerInfo: values.ownershipType === FormVehicleOwnershipType.NotCompanyOwned ? values.ownerInfo : null,
            leaseFromInfo: values.ownershipType === FormVehicleOwnershipType.CompanyOwnedLeasedFrom && values.leaseFromInfo && (values.leaseFromInfo.lessorId || (values.leaseFromInfo.lessorName && values.leaseFromInfo.lessorName.trim() !== '')) ? values.leaseFromInfo : null,
            leaseToInfo: values.ownershipType === FormVehicleOwnershipType.CompanyOwnedLeasedTo && values.leaseToInfo && (values.leaseToInfo.lesseeId || (values.leaseToInfo.lesseeName && values.leaseToInfo.lesseeName.trim() !== '')) ? values.leaseToInfo : null,
            dotInspectionExpiration: values.dotInspectionExpiration,
            platesExpirationDate: values.platesExpirationDate,
            insuranceExpirationDate: values.insuranceExpirationDate,
            insuranceValue: values.insuranceValue,
            escrowStartingBalance: values.escrowStartingBalance,
            escrowAccountBalance: values.escrowAccountBalance,
            hasCustomWarning: values.hasCustomWarning,
            iftaHandledByCompany: values.iftaHandledByCompany,
            iftaExpirationDate: values.iftaExpirationDate,
            nyPermitNumber: values.nyPermitNumber,
            grossWeight: values.grossWeight,
            unloadedWeight: values.unloadedWeight,
            numberOfAxles: values.numberOfAxles,
            dispatcher: values.dispatcher,
            dispatchGroup: values.dispatchGroup,
            dispatchCategory: values.dispatchCategory,
            gpsType: values.gpsType,
            lastOdometerReading: values.lastOdometerReading,
            lastOdometerDate: values.lastOdometerDate,
            estimatedOdometer: values.estimatedOdometer,
            comments: values.comments,
          };

          return await updateVehicle({ id: vehicleId, vehicle: updateRequest }).unwrap();
        } else {
          // For create mode, we need to transform the form values to match the API
          // For now, we'll use updateVehicle mutation which has the correct structure
          const createRequest: CreateVehicleRequest = {
            name: values.name,
            vin: values.vin,
            make: values.make,
            model: values.model,
            year: values.year,
            unitNumber: values.unitNumber,
            isActive: values.isActive,
            startDate: values.startDate,
            endDate: values.endDate,
            vehicleTypeId: values.vehicleTypeId,
            vehicleTypeName: values.vehicleTypeName,
            vehicleStatusId: values.vehicleStatusId,
            vehicleStatusName: values.vehicleStatusName,
            divisionId: values.divisionId,
            divisionName: values.divisionName,
            color: values.color,
            licensePlate: values.licensePlate,
            licenseState: values.licenseState,
            fuelCardNumber: values.fuelCardNumber,
            fuelType: values.fuelType,
            iPassNumber: values.iPassNumber,
            bestPassNumber: values.bestPassNumber,
            prePassEzPassNumber: values.prePassEzPassNumber,
            tireSize: values.tireSize,
            deductTolls: values.deductTolls,
            deductFuel: values.deductFuel,
            ownershipType: values.ownershipType,
            ownerInfo: values.ownershipType === FormVehicleOwnershipType.NotCompanyOwned ? values.ownerInfo : null,
            leaseFromInfo: values.leaseFromInfo,
            leaseToInfo: values.leaseToInfo,
            dotInspectionExpiration: values.dotInspectionExpiration,
            platesExpirationDate: values.platesExpirationDate,
            insuranceExpirationDate: values.insuranceExpirationDate,
            insuranceValue: values.insuranceValue,
            escrowStartingBalance: values.escrowStartingBalance,
            escrowAccountBalance: values.escrowAccountBalance,
            hasCustomWarning: values.hasCustomWarning,
            iftaHandledByCompany: values.iftaHandledByCompany,
            iftaExpirationDate: values.iftaExpirationDate,
            nyPermitNumber: values.nyPermitNumber,
            grossWeight: values.grossWeight,
            unloadedWeight: values.unloadedWeight,
            numberOfAxles: values.numberOfAxles,
            dispatcher: values.dispatcher,
            dispatchGroup: values.dispatchGroup,
            dispatchCategory: values.dispatchCategory,
            gpsType: values.gpsType,
            lastOdometerReading: values.lastOdometerReading,
            lastOdometerDate: values.lastOdometerDate,
            estimatedOdometer: values.estimatedOdometer,
            comments: values.comments,
          };

          return await createVehicle(createRequest).unwrap();
        }
      });
    },
  });

  const completionPercentage = useFormCompletion(formik.values);
  const {
    isSubmitting,
    submitError,
    successMessage,
    showValidationErrors,
    handleSubmit
  } = useFormSubmission({
    onSuccess: () => {
      setTimeout(() => {
        onSuccess();
      }, 1500);
    },
    successMessage: mode === "edit" ? "Vehicle updated successfully!" : "Vehicle created successfully!",
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleBack = () => {
    navigate("/vehicles");
  };

  // Helper function to get user-friendly error messages
  const getErrorMessage = (error: any): string => {
    if (error?.data?.message) {
      return error.data.message;
    }
    if (error?.data?.title) {
      return error.data.title;
    }
    if (error?.message) {
      return error.message;
    }
    if (error?.status === 400) {
      return "Invalid data provided. Please check your input and try again.";
    }
    if (error?.status === 404) {
      return "Vehicle not found. It may have been deleted.";
    }
    if (error?.status === 403) {
      return "You don't have permission to update this vehicle.";
    }
    if (error?.status >= 500) {
      return "Server error occurred. Please try again later.";
    }
    return "An unexpected error occurred. Please try again.";
  };

  if (mode === "edit" && (isLoading || (!vehicle && !error))) {
    return (
      <BaseFormPage
        title={title}
        onBack={handleBack}
        formik={formik}
        isLoading={true}
      >
        <div />
      </BaseFormPage>
    );
  }

  if (mode === "edit" && (error || !vehicle)) {
    return (
      <BaseFormPage
        title={title}
        onBack={handleBack}
        formik={formik}
        errorMessage={error ? getErrorMessage(error) : "Vehicle not found"}
      >
        <div />
      </BaseFormPage>
    );
  }

  const hasTelematics = Boolean(vehicle?.telematicsProvider);
  const identityEditable = mode === "create" || !hasTelematics;

  return (
    <BaseFormPage
      title={title}
      subtitle={mode === "edit" && vehicle ? vehicle.name : undefined}
      onBack={handleBack}
      formik={formik}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={submitError}
      completionPercentage={completionPercentage}
      showValidationErrors={showValidationErrors}
      submitButtonText={mode === "edit" ? "Save Changes" : "Create Vehicle"}
    >
      {/* Identity overview is read-only only when telematics owns those fields */}
      {mode === "edit" && vehicle && hasTelematics && (
        <Card sx={{ mb: { xs: 1, sm: 2, md: 3 } }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Vehicle Overview (Read-Only - Data from {vehicle.telematicsProvider})
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Make</Typography>
                <Typography variant="body1">{vehicle.make || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Model</Typography>
                <Typography variant="body1">{vehicle.model || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Year</Typography>
                <Typography variant="body1">{vehicle.year || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">VIN</Typography>
                <Typography variant="body1">{vehicle.vin || "-"}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Editable Form Tabs */}
      <Card>
        <CardContent>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              mb: 2,
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': { opacity: 0.3 },
              },
            }}
          >
            <Tab label="General" />
            <Tab label="Truck Info" />
            <Tab label="Ownership" />
            <Tab label="Safety" />
            {/* <Tab label="IFTA" /> */}
            <Tab label="Dispatch" />
            <Tab label="GPS" />
            <Tab label="Odometer" />
            <Tab label="Comments" />
            <Tab label="Documents" />
            {mode === "edit" && <Tab label="PTI History" />}
          </Tabs>

          <Box sx={{ mt: 2 }}>
            <BaseTabPanel value={selectedTab} index={0} idPrefix="vehicle">
              <GeneralTab
                availableVehicleTypes={vehicle?.availableVehicleTypes || []}
                availableVehicleStatuses={vehicle?.availableVehicleStatuses || []}
                availableDivisions={vehicle?.availableDivisions || []}
                identityEditable={identityEditable}
                hasTelematics={hasTelematics}
              />
            </BaseTabPanel>

            <BaseTabPanel value={selectedTab} index={1} idPrefix="vehicle">
              <TruckInfoTab />
            </BaseTabPanel>

            <BaseTabPanel value={selectedTab} index={2} idPrefix="vehicle">
              <OwnershipTab
                availableLessors={vehicle?.availableLessors || []}
                availableLessees={vehicle?.availableLessees || []}
              />
            </BaseTabPanel>

            <BaseTabPanel value={selectedTab} index={3} idPrefix="vehicle">
              <SafetyTab />
            </BaseTabPanel>

            {/* <BaseTabPanel value={selectedTab} index={4} idPrefix="vehicle">
              <IftaTab />
            </BaseTabPanel> */}

            <BaseTabPanel value={selectedTab} index={4} idPrefix="vehicle">
              <DispatchTab />
            </BaseTabPanel>

            <BaseTabPanel value={selectedTab} index={5} idPrefix="vehicle">
              <GpsTab />
            </BaseTabPanel>

            <BaseTabPanel value={selectedTab} index={6} idPrefix="vehicle">
              <OdometerTab />
            </BaseTabPanel>

            <BaseTabPanel value={selectedTab} index={7} idPrefix="vehicle">
              <CommentsTab />
            </BaseTabPanel>

            <BaseTabPanel value={selectedTab} index={8} idPrefix="vehicle">
              <VehicleDocumentsTab vehicleId={vehicleId} />
            </BaseTabPanel>
            {mode === "edit" && (
              <BaseTabPanel value={selectedTab} index={10} idPrefix="vehicle">
                <VehiclePtiHistoryTab vehicleId={vehicleId} />
              </BaseTabPanel>
            )}
          </Box>
        </CardContent>
      </Card>
    </BaseFormPage>
  );
};

export default VehicleFormLayout;
